import { useState, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { collectDiagnostics } from '@/services/diagnosticsCollector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Bug, Upload, X, Loader2, ImagePlus, CheckCircle2, Info,
} from 'lucide-react';

const CATEGORIES = [
  { value: 'bug', label: 'Bug / Something Broken' },
  { value: 'workout_issue', label: 'Workout Issue' },
  { value: 'billing_issue', label: 'Billing Issue' },
  { value: 'account_issue', label: 'Account Issue' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'other', label: 'Other' },
];

const MAX_ATTACHMENTS = 2;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

export function ReportIssueModal({ open, onOpenChange }) {
  const { api } = useAuth();
  const fileInputRef = useRef(null);

  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [diagnosticsConsented, setDiagnosticsConsented] = useState(true);
  const [attachments, setAttachments] = useState([]); // { upload_id, filename, uploading, error }
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [refCode, setRefCode] = useState('');

  const reset = useCallback(() => {
    setCategory('');
    setSubject('');
    setDescription('');
    setDiagnosticsConsented(true);
    setAttachments([]);
    setSubmitting(false);
    setSubmitted(false);
    setRefCode('');
  }, []);

  const handleClose = useCallback(() => {
    onOpenChange(false);
    // Delay reset so close animation finishes
    setTimeout(reset, 300);
  }, [onOpenChange, reset]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = '';

    for (const file of files) {
      if (attachments.length >= MAX_ATTACHMENTS) {
        toast.error(`Maximum ${MAX_ATTACHMENTS} attachments allowed`);
        break;
      }
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: Only PNG, JPEG, and WebP images are accepted`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: File must be under 5MB`);
        continue;
      }
      // Start upload
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      setAttachments((prev) => [...prev, { id: tempId, filename: file.name, uploading: true, error: null, upload_id: null }]);

      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/reports/upload-attachment', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setAttachments((prev) =>
          prev.map((a) => (a.id === tempId ? { ...a, uploading: false, upload_id: res.data.upload_id, filename: res.data.original_filename } : a))
        );
      } catch (err) {
        const msg = err.response?.data?.detail || 'Upload failed';
        setAttachments((prev) =>
          prev.map((a) => (a.id === tempId ? { ...a, uploading: false, error: msg } : a))
        );
        toast.error(msg);
      }
    }
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category) { toast.error('Please select a category'); return; }
    if (!subject.trim()) { toast.error('Please enter a subject'); return; }
    if (!description.trim()) { toast.error('Please describe the issue'); return; }

    const successUploads = attachments.filter((a) => a.upload_id && !a.error);
    const stillUploading = attachments.some((a) => a.uploading);
    if (stillUploading) { toast.error('Please wait for uploads to finish'); return; }

    setSubmitting(true);
    try {
      const diagnostics = diagnosticsConsented ? collectDiagnostics() : null;
      const payload = {
        category,
        subject: subject.trim(),
        description: description.trim(),
        upload_ids: successUploads.map((a) => a.upload_id),
        diagnostics_consented: diagnosticsConsented,
        diagnostics,
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
      };
      const res = await api.post('/reports', payload);
      setRefCode(res.data.ref_code);
      setSubmitted(true);
      toast.success('Report submitted successfully');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to submit report';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Success state
  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md" data-testid="report-success-dialog">
          <div className="flex flex-col items-center text-center py-6 gap-4">
            <div className="p-3 rounded-full bg-green-500/10">
              <CheckCircle2 size={40} className="text-green-500" />
            </div>
            <div>
              <h3 className="text-lg font-heading font-bold">Report Submitted</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Thank you for helping us improve BodyMax.
              </p>
            </div>
            <div className="bg-muted rounded-lg px-4 py-3 w-full" data-testid="report-ref-code">
              <p className="text-xs text-muted-foreground mb-1">Reference Code</p>
              <p className="font-mono text-lg font-bold tracking-wider">{refCode}</p>
            </div>
            <p className="text-xs text-muted-foreground">
              Save this code if you need to follow up with support.
            </p>
            <Button onClick={handleClose} className="bg-accent text-accent-foreground hover:bg-primary/90 rounded-full w-full" data-testid="report-done-btn">
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="report-issue-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug size={20} className="text-accent" />
            Report an Issue
          </DialogTitle>
          <DialogDescription>
            Tell us what went wrong and we'll look into it.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="report-issue-form">
          {/* Category */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Category</Label>
            <Select value={category} onValueChange={setCategory} data-testid="report-category-select">
              <SelectTrigger className="rounded-lg" data-testid="report-category-trigger">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value} data-testid={`report-category-${c.value}`}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Subject */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary of the issue"
              maxLength={100}
              className="rounded-lg"
              data-testid="report-subject-input"
            />
            <p className="text-xs text-muted-foreground text-right">{subject.length}/100</p>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What happened? What were you trying to do?"
              maxLength={2000}
              rows={4}
              className="rounded-lg resize-none"
              data-testid="report-description-input"
            />
            <p className="text-xs text-muted-foreground text-right">{description.length}/2000</p>
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <Label className="text-xs font-medium">Screenshots (optional, max 2)</Label>
            <div className="flex flex-wrap gap-2">
              {attachments.map((att) => (
                <div
                  key={att.id}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs border ${
                    att.error ? 'border-red-500/50 bg-red-500/5' : att.uploading ? 'border-border bg-muted/50' : 'border-accent/30 bg-accent/5'
                  }`}
                  data-testid={`report-attachment-${att.id}`}
                >
                  {att.uploading && <Loader2 size={12} className="animate-spin" />}
                  {att.error && <X size={12} className="text-red-500" />}
                  {!att.uploading && !att.error && <ImagePlus size={12} className="text-accent" />}
                  <span className="max-w-[120px] truncate">{att.filename}</span>
                  <button type="button" onClick={() => removeAttachment(att.id)} className="hover:text-red-500 transition-colors">
                    <X size={12} />
                  </button>
                </div>
              ))}
              {attachments.length < MAX_ATTACHMENTS && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-dashed border-border hover:border-accent/50 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="report-add-attachment-btn"
                >
                  <Upload size={12} /> Add image
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Diagnostics toggle */}
          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 border border-border">
            <Switch
              checked={diagnosticsConsented}
              onCheckedChange={setDiagnosticsConsented}
              className="mt-0.5"
              data-testid="report-diagnostics-toggle"
            />
            <div className="flex-1">
              <p className="text-sm font-medium">Include app diagnostics</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Shares your current page, screen size, and recent errors to help us diagnose the issue. No personal data or tokens are included.
              </p>
            </div>
            <Info size={14} className="text-muted-foreground shrink-0 mt-0.5" />
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={submitting || !category || !subject.trim() || !description.trim()}
            className="w-full bg-accent text-accent-foreground hover:bg-primary/90 rounded-full font-semibold gap-2"
            data-testid="report-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Submitting...
              </>
            ) : (
              <>
                <Bug size={16} /> Submit Report
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
